import { computed, ref, watch } from 'vue';
import { useAlert } from 'dashboard/composables';

const ACTIVE_LIFECYCLE_STATUSES = new Set(['active', 'live']);

const normalizeName = value =>
  String(value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const normalizedAssistantId = value => {
  const assistantId = Number(value);
  return Number.isInteger(assistantId) && assistantId > 0 ? assistantId : null;
};

const assistantLifecycleStatuses = assistant => [
  assistant?.agent_configuration?.lifecycle?.status,
  assistant?.agent_configuration?.lifecycle_status,
  assistant?.config?.publication_mode,
];

const isActiveAssistant = assistant =>
  assistantLifecycleStatuses(assistant).some(status =>
    ACTIVE_LIFECYCLE_STATUSES.has(String(status || '').toLowerCase())
  );

export const resolveToolsAssistant = ({
  assistants = [],
  queryAssistantId,
  lastActiveAssistantId,
} = {}) => {
  if (!assistants.length) return null;

  const requestedId = normalizedAssistantId(queryAssistantId);
  const requested = assistants.find(
    assistant => Number(assistant.id) === requestedId
  );
  if (requested) return requested;

  const exactSolicitacoes = assistants.find(
    assistant => normalizeName(assistant.name) === 'assistente solicitacoes'
  );
  if (exactSolicitacoes) return exactSolicitacoes;

  const solicitacoes = assistants.find(assistant =>
    normalizeName(assistant.name).includes('solicitacoes')
  );
  if (solicitacoes) return solicitacoes;

  const activeId = normalizedAssistantId(lastActiveAssistantId);
  const lastActive = assistants.find(
    assistant => Number(assistant.id) === activeId
  );
  if (lastActive) return lastActive;

  return assistants.find(isActiveAssistant) || assistants[0];
};

export const useToolsAssistantContext = ({
  store,
  route,
  router,
  uiSettings,
  t,
  notify = useAlert,
}) => {
  const selectedAssistantId = ref(null);
  const assistantContextError = ref('');
  const assistantsLoaded = ref(false);

  const assistants = computed(
    () => store.getters['captainAssistants/getRecords'] || []
  );
  const assistantsUiFlags = computed(
    () => store.getters['captainAssistants/getUIFlags'] || {}
  );
  const isFetchingAssistants = computed(() =>
    Boolean(assistantsUiFlags.value.fetchingList)
  );

  const assistantById = value => {
    const assistantId = normalizedAssistantId(value);
    return assistants.value.find(
      assistant => Number(assistant.id) === assistantId
    );
  };

  const replaceAssistantQuery = async assistantId => {
    if (String(route.query.assistant_id || '') === String(assistantId)) return;

    try {
      await router.replace({
        name: route.name || 'captain_account_tools_index',
        params: { accountId: route.params.accountId },
        query: {
          ...route.query,
          assistant_id: String(assistantId),
        },
      });
    } catch (error) {
      notify(
        error?.message ||
          t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.AGENT_QUERY_UPDATE_ERROR')
      );
    }
  };

  const applyResolvedAssistant = async () => {
    const assistant = resolveToolsAssistant({
      assistants: assistants.value,
      queryAssistantId: route.query.assistant_id,
      lastActiveAssistantId: uiSettings.value?.last_active_assistant_id,
    });

    selectedAssistantId.value = assistant ? Number(assistant.id) : null;
    if (assistant) await replaceAssistantQuery(assistant.id);
    return assistant;
  };

  const loadAssistants = async () => {
    assistantContextError.value = '';
    try {
      await store.dispatch('captainAssistants/get', {
        page: 1,
        perPage: 100,
      });
      assistantsLoaded.value = true;
      return applyResolvedAssistant();
    } catch (error) {
      assistantsLoaded.value = true;
      selectedAssistantId.value = null;
      assistantContextError.value =
        error?.message ||
        t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.AGENTS_LOAD_ERROR');
      return null;
    }
  };

  const selectAssistant = async value => {
    const assistant = assistantById(value);
    if (!assistant) return false;

    selectedAssistantId.value = Number(assistant.id);
    await replaceAssistantQuery(assistant.id);
    return true;
  };

  watch(
    () => route.query.assistant_id,
    () => {
      if (assistantsLoaded.value) applyResolvedAssistant();
    }
  );

  return {
    assistants,
    selectedAssistantId,
    assistantContextError,
    isFetchingAssistants,
    loadAssistants,
    selectAssistant,
  };
};
